// ============================================================================
// SHIPPING CONFIGURATION - CONSOLIDATED DATA SOURCE
// ============================================================================
// This is the SINGLE SOURCE OF TRUTH for all shipping data
// All other objects are generated from this for backward compatibility
// ============================================================================

const SHIPPING_DATA = {
  // EU Countries (9 EUR)
  "AT": { amount: 9, currency: "EUR", threshold: 9800 },
  "BE": { amount: 9, currency: "EUR", threshold: 9800 },
  "CZ": { amount: 9, currency: "EUR", threshold: 9800 },
  "DE": { amount: 9, currency: "EUR", threshold: 9800 },
  "DK": { amount: 9, currency: "EUR", threshold: 9800 },
  "EE": { amount: 9, currency: "EUR", threshold: 9800 },
  "FI": { amount: 9, currency: "EUR", threshold: 9800 },
  "FR": { amount: 9, currency: "EUR", threshold: 9800 },
  "GR": { amount: 9, currency: "EUR", threshold: 9800 },
  "IE": { amount: 9, currency: "EUR", threshold: 9800 },
  "IT": { amount: 9, currency: "EUR", threshold: 9800 },
  "IS": { amount: 9, currency: "EUR", threshold: 9800 },
  "LT": { amount: 9, currency: "EUR", threshold: 9800 },
  "LU": { amount: 9, currency: "EUR", threshold: 9800 },
  "LV": { amount: 9, currency: "EUR", threshold: 9800 },
  "NL": { amount: 9, currency: "EUR", threshold: 9800 },
  "PL": { amount: 9, currency: "EUR", threshold: 9800 },
  "PT": { amount: 9, currency: "EUR", threshold: 9800 },
  "SI": { amount: 9, currency: "EUR", threshold: 9800 },
  "ES": { amount: 9, currency: "EUR", threshold: 9800 },
  
  // North America
  "US": { amount: 35, currency: "USD", threshold: 35000 },
  "USA": { amount: 35, currency: "USD", threshold: 35000 },
  "CA": { amount: 40, currency: "CAD", threshold: 45000 },
  
  // Oceania
  "AU": { amount: 49, currency: "AUD", threshold: 49500 },
  
  // Asia
  "SG": { amount: 39, currency: "SGD", threshold: 39900 },
  "AE": { amount: 117, currency: "AED", threshold: 120000 },
  
  // Europe (non-EU or special rates)
  "NO": { amount: 240, currency: "NOK", threshold: 250000 },
  "UK": { amount: 23, currency: "GBP", threshold: 23000 },
  "GB": { amount: 23, currency: "GBP", threshold: 23000 },
  "GBR": { amount: 23, currency: "GBP", threshold: 23000 },
  "CH": { amount: 25, currency: "CHF", threshold: 24000 },
  "CY": { amount: 29, currency: "EUR", threshold: 24500 },
  "SE": { amount: 290, currency: "SEK", threshold: 260000 },
};

// Default fallback values
const DEFAULT_SHIPPING = {
  amount: null,
  currency: null,
  threshold: 27500
};

// ============================================================================
// NEW IMPROVED FUNCTION - Get all shipping info at once
// ============================================================================
function getShippingInfo(country_code) {
  const data = SHIPPING_DATA[country_code];
  if (!data) {
    return { ...DEFAULT_SHIPPING };
  }
  return { ...data };
}

// ============================================================================
// BACKWARD COMPATIBILITY - Generate legacy objects
// ============================================================================
// These are generated from SHIPPING_DATA to maintain compatibility
// with existing code that uses shippingCosts and thresholds

const shippingCosts = {};
const SHIPPING_THRESHOLDS = {};

// Generate shippingCosts and thresholds from SHIPPING_DATA
Object.keys(SHIPPING_DATA).forEach(countryCode => {
  const data = SHIPPING_DATA[countryCode];
  
  // Generate shippingCosts (legacy format)
  shippingCosts[countryCode] = {
    amount: data.amount,
    currency: data.currency
  };
  
  // Generate thresholds by shipping cost amount
  // Multiple countries can have the same cost, so we only set it once
  if (!SHIPPING_THRESHOLDS[data.amount]) {
    SHIPPING_THRESHOLDS[data.amount] = data.threshold;
  }
});

// Add default fallback to thresholds
SHIPPING_THRESHOLDS[null] = DEFAULT_SHIPPING.threshold;
SHIPPING_THRESHOLDS[""] = DEFAULT_SHIPPING.threshold;

// ============================================================================
// EXISTING FUNCTIONS - Maintained for backward compatibility
// ============================================================================

// Function to get shipping cost - returns amount with currency info
// IMPORTANT: Currency is determined by LOCATION (via Shopify Markets), not browser language
// Example: Canadian with Spanish browser = CAD currency, Spanish language
function getShippingCost(country_code) {
  const costInfo = shippingCosts[country_code];
  if (!costInfo) return null;
  
  // Get the current cart currency from Shopify Markets
  // This is based on the customer's LOCATION, not their browser language
  const currentCurrency = window.customStrings?.currency_code || 
                         window.Shopify?.currency?.active || 
                         null;
  
  // Return the shipping cost amount for this country
  // The currency symbol will be added from cart.currency.symbol (which is location-based)
  // This ensures: Canadian customer = $45 CAD, regardless of browser language
  return costInfo.amount;
}

// Function to get threshold by shipping cost amount (for backward compatibility)
function getThresholdByShippingCost(shippingCost) {
  // Handle string parsing (e.g., "9" or "9 €" -> 9)
  let costKey = shippingCost;
  if (typeof shippingCost === 'string') {
    const numericMatch = shippingCost.match(/\d+/);
    costKey = numericMatch ? parseInt(numericMatch[0], 10) : null;
  } else if (shippingCost != null) {
    costKey = parseInt(shippingCost, 10);
  }
  
  return SHIPPING_THRESHOLDS[costKey] || 
         SHIPPING_THRESHOLDS[null] || 
         DEFAULT_SHIPPING.threshold;
}

// Function to get country using Shopify Markets
async function getShopifyCountry() {
  try {
    // Primer intento: usar window.Shopify.country (disponible con Markets)
    if (window.Shopify && window.Shopify.country) {
      return {
        country_code: window.Shopify.country,
        country_name: getCountryName(window.Shopify.country)
      };
    }
    
    // Segundo intento: usar la API nativa de Shopify
    const response = await fetch('browsing_context_suggestions.json');
    if (response.ok) {
      const data = await response.json();
      // Always translate country name using our function to match current page language
      return {
        country_code: data.detected_values.country.iso_code,
        country_name: getCountryName(data.detected_values.country.iso_code)
      };
    }
    
    // Fallback a España
    return {
      country_code: 'ES',
      country_name: getCountryName('ES')
    };
  } catch (error) {
    console.error('Error getting country from Shopify:', error);
    return {
      country_code: 'ES',
      country_name: getCountryName('ES')
    };
  }
}

// Helper function to get country name from code (translated based on current language)
// Uses native browser APIs for automatic translation - no hardcoded languages needed
function getCountryName(countryCode) {
  // Get current language/locale from multiple sources
  let currentLocale = 'en'; // default to English
  
  // Try to get full locale from window.themeTranslations (set in main-product.liquid)
  if (window.themeTranslations && window.themeTranslations.language) {
    currentLocale = window.themeTranslations.language;
  }
  // Fallback to HTML lang attribute (e.g., "es-ES", "it-IT")
  else if (document.documentElement && document.documentElement.lang) {
    currentLocale = document.documentElement.lang;
  }
  // Fallback to Shopify locale
  else if (window.Shopify && window.Shopify.locale) {
    currentLocale = window.Shopify.locale;
  }
  
  // Use Intl.DisplayNames API for automatic translation (works with any language)
  if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
    const langCode = currentLocale.split('-')[0].toLowerCase();
    
    // Try different locale formats to ensure compatibility
    // Build locale variants: full locale, language-uppercase, just language
    const localeVariants = [];
    if (currentLocale.includes('-')) {
      localeVariants.push(currentLocale); // "es-ES"
    }
    localeVariants.push(langCode + '-' + langCode.toUpperCase()); // "es-ES" from "es"
    localeVariants.push(langCode); // "es"
    
    // Remove duplicates
    const uniqueLocales = [...new Set(localeVariants)];
    
    for (const locale of uniqueLocales) {
      try {
        const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
        const countryName = displayNames.of(countryCode);
        if (countryName && countryName !== countryCode) {
          return countryName;
        }
      } catch (e) {
        // Try next locale variant
        continue;
      }
    }
  }
  
  // Final fallback: return the country code if translation fails
  return countryCode;
}

// ============================================================================
// NEW IMPROVED HELPER FUNCTION - Check free shipping with all info
// ============================================================================
// This is an improved version that returns all necessary information
// Can be used optionally in new code, but doesn't break existing code
async function checkFreeShipping(countryCode, productPrice = 0) {
  try {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error("Failed to fetch cart data");
    const data = await response.json();
    
    const cartTotal = data.items_subtotal_price || 0;
    const totalPrice = cartTotal + productPrice;
    
    const shippingInfo = getShippingInfo(countryCode);
    
    return {
      qualifies: totalPrice >= shippingInfo.threshold,
      currentTotal: totalPrice,
      threshold: shippingInfo.threshold,
      remaining: Math.max(0, shippingInfo.threshold - totalPrice),
      shippingCost: shippingInfo.amount,
      currency: shippingInfo.currency
    };
  } catch (error) {
    console.error("Error checking free shipping:", error);
    return {
      qualifies: false,
      error: true,
      threshold: DEFAULT_SHIPPING.threshold
    };
  }
}

// ============================================================================
// INITIALIZATION - Same as before, maintains compatibility
// ============================================================================

(async function () {
  try {
    const storedData = localStorage.getItem('themeData');
    const currentTime = Date.now();
    let themeData;
    
    // Siempre obtener el país actual desde Shopify Markets para verificar
    const currentCountryData = await getShopifyCountry();
    const currentCountryCode = currentCountryData.country_code;
    
    if (storedData) {
      const stored = JSON.parse(storedData);
      const { country_code, expiry } = stored;
      
      // Si el país guardado es diferente al detectado actualmente, limpiar y actualizar
      if (country_code !== currentCountryCode) {
        console.log('🔄 [shipping-cost] País detectado diferente al guardado:', {
          guardado: country_code,
          detectado: currentCountryCode,
          accion: 'Limpiando localStorage y actualizando'
        });
        localStorage.removeItem('themeData');
        stored = null; // Forzar nueva detección
      }
      // Check if the data is still valid (4 hours expiration - menos tiempo ya que es más rápido)
      else if (expiry > currentTime) {
        // Always retranslate country name to match current page language
        themeData = {
          ...stored,
          country_code: country_code,
          country_name: getCountryName(country_code), // Always translate based on current language
          shippingCost: getShippingCost(country_code)
        };
        console.log('✅ [shipping-cost] Usando datos de localStorage (válidos):', themeData);
      } else {
        localStorage.removeItem('themeData'); // Clear expired data
        console.log('⏰ [shipping-cost] Datos expirados, limpiando localStorage');
      }
    }
    
    // If there's no valid themeData, get country from Shopify Markets
    if (!themeData) {
      console.log('🌍 [shipping-cost] Obteniendo país desde Shopify Markets:', currentCountryCode);
      
      themeData = {
        country_code: currentCountryCode,
        country_name: getCountryName(currentCountryCode), // Ensure translation
        shippingCost: getShippingCost(currentCountryCode),
        expiry: currentTime + (4 * 60 * 60 * 1000) // Cache for 4 hours (reducido)
      };
      
      localStorage.setItem('themeData', JSON.stringify(themeData));
      console.log('💾 [shipping-cost] Guardado en localStorage:', themeData);
    }
    
    // Always ensure country_name is translated to current language (in case language changed)
    // This ensures the country name matches the current page language
    themeData.country_name = getCountryName(themeData.country_code);  
    
    // If themeTranslations becomes available later, update country name
    if (!window.themeTranslations || !window.themeTranslations.language) {
      const checkThemeTranslations = setInterval(() => {
        if (window.themeTranslations && window.themeTranslations.language) {
          const newCountryName = getCountryName(themeData.country_code);
          if (newCountryName !== themeData.country_name) {
            themeData.country_name = newCountryName;
            window.themeData = themeData;
            console.log('Nombre del país actualizado:', newCountryName);
          }
          clearInterval(checkThemeTranslations);
        }
      }, 50);
      
      // Stop checking after 2 seconds
      setTimeout(() => clearInterval(checkThemeTranslations), 2000);
    }
    
    // Hacer themeData disponible globalmente para debugging
    window.themeData = themeData;
    
    // Export thresholds for use in other files (backward compatibility)
    // This allows cart-drawer.js and new_layout.liquid to use it
    window.SHIPPING_THRESHOLDS = SHIPPING_THRESHOLDS;
    
  } catch (error) {
    console.error('Error:', error);
    // Fallback en caso de error total
    const fallbackData = {
      country_code: 'ES',
      country_name: getCountryName('ES'),
      shippingCost: getShippingCost('ES')
    };
    window.themeData = fallbackData;
    console.log('Usando fallback:', fallbackData);
  }
})();
